import { useState, useEffect, useContext, useMemo } from "react";
import AgGridLayout from "@wrappers/layouts/AgGridLayout";
import { GridApi, ValueFormatterParams } from "ag-grid-community";
import { useDispatch, useSelector } from "react-redux";
import Layout from "@wrappers/layouts/main-layout/Layout";
import { useClearGridData, useGetModulesData, usePermissionCheck } from "@hooks/AllCustomHooks";
import { mainContext } from "@ts/constants";
import { customFilterOptions } from "@ts/constants";
import {
    CampaignAnchorCellRenderer,
    ChangeInValueCellRenderer,
    FilterTextFormatter,
    KeywordCellRenderer,
    PopOverNameCellRenderer,
    ProgramTypeCellRenderer,
} from "@cell-renderers/AllCellRenderers";
import { createDatasource } from "@ts/retrieve-data";
import {
    titleCase,
    onKeywordSelectionChanged,
    numericValueFormatter,
    getCampaignsAndPortfolios,
} from "@ts/utils";
import { getDateObj } from "@ts/date-range";
import { setIsLoader } from "@features/loaderSlice";
import { updateTitle } from "@features/titleSlice";
import { updateActiveModule } from "@features/activeModuleSlice";
import AddRulesModal from "@modals/rules-modal/AddRulesModal";
import * as Sentry from "@sentry/react";
import {
    IChannel,
    IMainContext,
    Keyword,
    ProgramTypeText,
    TActiveClientId,
    TCampaignPortfolioList,
} from "@ts/common-types";
import { compact, map, reduce } from "underscore";

const KeywordAnalytics = () => {
    const clearGridData = useClearGridData();
    const [rowData, setRowData] = useState<Keyword[]>();
    const [keywordsData, setKeywordsData] = useState<Keyword[]>([]);
    const [gridApi, setGridApi] = useState<GridApi>();
    const activeClientId = useSelector(
        (state: TActiveClientId) => state.activeClientId.value
    );
    const campaignPortfolioData = useSelector(
        (state: TCampaignPortfolioList) => state.campaignPortfolioData.value
    );
    const dispatch = useDispatch();
    const { dates, DateRange } = useContext<IMainContext>(mainContext);
    const [loading, setLoading] = useState(true);
    const channel = useSelector((state: IChannel) => state.channel.value);
    const gridParams = useGetModulesData({ type: "grid-params" });

    const [isDataRetrieved, setIsDataRetrieved] = useState<boolean>(false);

    const populateKeywordAnalytics = async () => {
        return await createDatasource("keyword-analytics", {
            dateObj: getDateObj(),
        });
    };

    const { canCreateRule } = usePermissionCheck()

    useEffect(() => {
        clearGridData();
        setKeywordsData([]);
        dispatch(updateTitle("Search Term Analytics"));
        dispatch(updateActiveModule("keyword analytics"));
        dispatch(setIsLoader(true));
        setLoading(true);

        getCampaignsAndPortfolios()
            .then(() => populateKeywordAnalytics())
            .then((keywords) => {
                setIsDataRetrieved(true);
                setKeywordsData(keywords as Array<Keyword>);
            })
            .catch((err) => {
                console.error(err);
                Sentry.captureException(err);
            });

        // eslint-disable-next-line
    }, [activeClientId, dates, DateRange, dispatch, channel]);


    useEffect(() => {
        if (keywordsData.length && campaignPortfolioData.length) {
            const campaignPortfolioMap = reduce(
                campaignPortfolioData,
                (acc: any, item) => {
                    const {
                        sys_campaign_id,
                        portfolio_id,
                        campaign_name,
                        portfolio_name
                    } = item;

                    if (sys_campaign_id && !acc.campaigns[sys_campaign_id]
                    ) {
                        acc.campaigns[sys_campaign_id] = campaign_name;
                    }

                    if (portfolio_id && !acc.portfolios[portfolio_id]) {
                        acc.portfolios[portfolio_id] = portfolio_name;
                    }
                    return acc;
                },
                { campaigns: {}, portfolios: {} }
            );

            const modifiedRowData = map(keywordsData, (keyword) => {
                const campaigns = map(
                    keyword.campaign_map?.split(","),
                    (item) => +item
                );
                const portfolios = map(
                    keyword.portfolio_map?.split(","),
                    (item) => +item
                );

                const campaignNames = map(
                    campaigns,
                    (id) => campaignPortfolioMap.campaigns[id]
                );
                const portfolioNames = map(
                    portfolios,
                    (id) => campaignPortfolioMap.portfolios[id]
                );

                return {
                    ...keyword,
                    campaigns: compact(campaignNames),
                    portfolios: compact(portfolioNames),
                };
            });

            setRowData(modifiedRowData);
            setLoading(false);
            dispatch(setIsLoader(false));
        } else if (!keywordsData.length && isDataRetrieved) {
            setRowData([]);
            setLoading(false);
            dispatch(setIsLoader(false));
        }
        setIsDataRetrieved(false);
    }, [campaignPortfolioData, keywordsData, dispatch, isDataRetrieved]);

    const columnDefs = useGetModulesData({
        type: "columns",
        colDefs: [
            {
                field: "keyword_name",
                headerName: "Search Term",
                minWidth: 300,
                maxWidth: 450,
                filter: "agTextColumnFilter",
                pinned: "left",
                lockPinned: true,
                cellClass: "lock-pinned",
                checkboxSelection: true,
                tooltipField: "keyword_name",
                cellRenderer: KeywordCellRenderer,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    pinnedColumnValue: "Total",
                    isKeywordLinkRequired: gridParams?.isKeywordLinkRequired,
                },
                headerCheckboxSelection: true,
                headerCheckboxSelectionFilteredOnly: true,
            },
            {
                field: "campaigns",
                headerName: "Campaigns",
                filter: "agTextColumnFilter",
                cellRendererParams: {
                    hasPinnedColumnValue: false,
                },
                minWidth: 150,
                maxWidth: 160,
                cellRenderer: PopOverNameCellRenderer,
                hide: true,
            },
            {
                field: "portfolios",
                headerName: "Portfolios",
                filter: "agTextColumnFilter",
                cellRendererParams: {
                    hasPinnedColumnValue: false,
                },
                minWidth: 210,
                maxWidth: 220,
                cellRenderer: PopOverNameCellRenderer,
                hide: true,
            },
            {
                field: "kwd_class",
                headerName: "Keyword Class",
                filter: "agSetColumnFilter",
                cellRendererParams: {
                    hasPinnedColumnValue: false,
                },
                valueFormatter: (params: ValueFormatterParams) =>
                    params.value ? titleCase(params.value) : params.value,
                filterParams: {
                    valueFormatter: (params: ValueFormatterParams) =>
                        params.value ? titleCase(params.value) : params.value,
                },
                hide: true,
            },
            {
                field: "campaign_nos",
                headerName: "# Campaigns",
                filter: false,
                cellClass: "lock-pinned",
                valueFormatter: (params: ValueFormatterParams) =>
                    params.data.adgroup_count || 0,
                cellRenderer: CampaignAnchorCellRenderer,
                cellRendererParams: {
                    hasPinnedColumnValue: false,
                },
            },
            {
                field: "is_exact",
                headerName:
                    gridParams["is_exact"]?.headerName || "Target Present",
                filter: "agSetColumnFilter",
                cellRendererParams: {
                    hasPinnedColumnValue: false,
                },
                valueFormatter: (params: ValueFormatterParams) =>
                    (`` + params.value || "").toUpperCase(),
                hide: gridParams.columnsToHide?.includes("is_exact"),
            },
            {
                field: "impression_share_rank",
                headerName: "SP. Impr. Rank",
                filter: false,
                cellRenderer: ChangeInValueCellRenderer,
                valueFormatter: numericValueFormatter,
                cellRendererParams: {
                    hasPinnedColumnValue: false,
                    hasPercent: false,
                    showChangePercent: false,
                    suppressNullValueText: "N/A",
                    decimalPlace: 0,
                },
                hide: true,
            },
            // ToDo: Possibly create a config if field names would be different for different channels
            {
                field: "impression_share",
                headerName: "Impr. % Share",
                maxWidth: 180,
                minWidth: 140,
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                cellRenderer: ChangeInValueCellRenderer,
                valueFormatter: numericValueFormatter,
                cellRendererParams: {
                    hasPinnedColumnValue: false,
                    hasPercent: true,
                    showChangePercent: true,
                },
                hide: gridParams.columnsToHide?.includes("impression_share"),
            },
            {
                field: "page1OverallSov",
                headerName: "Overall SOV",
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                cellRenderer: ChangeInValueCellRenderer,
                minWidth: 180,
                valueFormatter: numericValueFormatter,
                cellRendererParams: {
                    hasPinnedColumnValue: false,
                    hasPercent: true,
                    showChangePercent: true,
                },
                hide: true,
            },
            {
                field: "page1OrganicSov",
                headerName: "Organic SOV",
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                cellRenderer: ChangeInValueCellRenderer,
                minWidth: 180,
                valueFormatter: numericValueFormatter,
                cellRendererParams: {
                    hasPinnedColumnValue: false,
                    hasPercent: true,
                    showChangePercent: true,
                },
                hide: gridParams.columnsToHide?.includes("page1OrganicSov"),
            },
            {
                field: "search_volume",
                headerName: "Search Vol .",
                minWidth: 120,
                maxWidth: 130,
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                valueFormatter: numericValueFormatter,
                cellRenderer: ChangeInValueCellRenderer,
                cellRendererParams: {
                    hasPercent: false,
                    showChangePercent: false,
                    hasPinnedColumnValue: false,
                },
            },
            {
                field: "sponsored_sov",
                headerName: "Sov",
                minWidth: 120,
                maxWidth: 130,
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                valueFormatter: numericValueFormatter,
                cellRenderer: ChangeInValueCellRenderer,
                cellRendererParams: {
                    hasPercent: true,
                    showChangePercent: false,
                    hasPinnedColumnValue: false,
                },
            },
            {
                field: "top_product",
                headerName: "Top Product",
                maxWidth: 150,
                tooltipField: "top_product",
                filter: "agTextColumnFilter",
                cellRendererParams: {
                    hasPinnedColumnValue: false,
                },
                valueFormatter: (params: ValueFormatterParams) =>
                    params.value !== "null" ? params.value : "",
                hide: true,
            },
            {
                headerName: "Impressions",
                field: "impression",
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                cellRenderer: ChangeInValueCellRenderer,
                minWidth: 180,
                valueFormatter: numericValueFormatter,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: false,
                    decimalPlace: 0,
                    showChangePercent: true,
                },
                hide: gridParams.columnsToHide?.includes("impression"),
            },
            {
                headerName: gridParams["click"]?.headerName || "Clicks",
                field: "click",
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                cellRenderer: ChangeInValueCellRenderer,
                minWidth: 180,
                valueFormatter: numericValueFormatter,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: false,
                    decimalPlace: 0,
                    showChangePercent: true,
                },
                hide: true,
            },
            {
                headerName: "Spends",
                field: "spend",
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                sort: "desc",
                cellRenderer: ChangeInValueCellRenderer,
                minWidth: 180,
                valueFormatter: numericValueFormatter,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: false,
                    showChangePercent: true,
                },
            },
            {
                headerName: "Orders",
                field: "orders",
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                cellRenderer: ChangeInValueCellRenderer,
                minWidth: 180,
                valueFormatter: numericValueFormatter,
                hide: true,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: false,
                    decimalPlace: 0,
                    showChangePercent: true,
                },
            },
            {
                headerName: "Sales",
                field: "sale",
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                cellRenderer: ChangeInValueCellRenderer,
                minWidth: 180,
                valueFormatter: numericValueFormatter,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: false,
                    showChangePercent: true,
                },
            },
            {
                headerName: "CTR",
                field: "ctr",
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                cellRenderer: ChangeInValueCellRenderer,
                minWidth: 180,
                valueFormatter: numericValueFormatter,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: true,
                    showChangePercent: true,
                    decimalPlace: 1,
                },
            },
            {
                headerName: gridParams["cpc"]?.headerName || "CPC",
                field: "cpc",
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                cellRenderer: ChangeInValueCellRenderer,
                minWidth: 180,
                valueFormatter: numericValueFormatter,
                hide: gridParams.columnsToHide?.includes("cpc"),
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: false,
                    showChangePercent: true,
                },
            },
            {
                headerName: "CPM",
                field: "cpm",
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                minWidth: 140,
                cellRenderer: ChangeInValueCellRenderer,
                valueFormatter: numericValueFormatter,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: false,
                    showChangePercent: true,
                },
            },
            {
                headerName: "CR",
                field: "cr",
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                cellRenderer: ChangeInValueCellRenderer,
                minWidth: 180,
                valueFormatter: numericValueFormatter,
                hide: true,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: true,
                    showChangePercent: true,
                },
            },
            {
                headerName: "CPA",
                field: "cpa",
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                cellRenderer: ChangeInValueCellRenderer,
                minWidth: 180,
                valueFormatter: numericValueFormatter,
                hide: true,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: false,
                    showChangePercent: true,
                },
            },
            {
                headerName: "ACoS",
                field: "acos",
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                hide: true,
                cellRenderer: ChangeInValueCellRenderer,
                minWidth: 180,
                valueFormatter: numericValueFormatter,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: true,
                    showChangePercent: true,
                },
            },
            {
                headerName: "Total Ad Sales",
                cellStyle: { whiteSpace: "normal" },
                field: "total_clicked_ad_sales",
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                valueFormatter: numericValueFormatter,
                cellRenderer: ChangeInValueCellRenderer,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: false,
                    showChangePercent: true,
                },
            },
            {
                field: "troas",
                headerName: "TRoAS",
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                cellRenderer: ChangeInValueCellRenderer,
                minWidth: 140,
                valueFormatter: numericValueFormatter,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: false,
                    showChangePercent: true,
                },
            },
            {
                field: "program_type",
                headerName: "Ad Type",
                filter: "agSetColumnFilter",
                cellRendererParams: {
                    hasPinnedColumnValue: false,
                },
                cellRenderer: ProgramTypeCellRenderer,
                valueFormatter: (params: ValueFormatterParams) =>
                    params.value ? params.value.toUpperCase() : params.value,
                filterParams: {
                    valueFormatter: FilterTextFormatter
                },
            },
            {
                field: "total_impression",
                headerName: "Total Impr.",
                minWidth: 200,
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                cellRenderer: ChangeInValueCellRenderer,
                valueFormatter: numericValueFormatter,
                cellRendererParams: {
                    hasPinnedColumnValue: false,
                    hasPercent: false,
                    showChangePercent: true,
                    decimalPlace: 1,
                },
                hide: gridParams.columnsToHide?.includes("total_impression"),
            },
            {
                field: "rank",
                headerName: "Organic Rank",
                filter: false,
                cellRenderer: ChangeInValueCellRenderer,
                valueFormatter: numericValueFormatter,
                cellRendererParams: {
                    hasPinnedColumnValue: false,
                    hasPercent: false,
                    showChangePercent: false,
                    suppressNullValueText: "N/A",
                    decimalPlace: 0,
                },
                hide: gridParams.columnsToHide?.includes("rank"),
            },
            {
                headerName: "RoAS",
                field: "roas",
                filter: "agNumberColumnFilter",
                filterParams: {
                    filterOptions: customFilterOptions["number"],
                },
                cellRenderer: ChangeInValueCellRenderer,
                minWidth: 180,
                valueFormatter: numericValueFormatter,
                cellRendererParams: {
                    hasPinnedColumnValue: true,
                    hasPercent: false,
                    showChangePercent: true,
                },
            },
        ],
    });

    return (
        <>
            <Layout>
                <div className="main-content-body subheader-fixed">
                    <div
                        className="wrapper"
                        style={{
                            flex: "1 1 0",
                            width: "100%",
                        }}
                    >
                        <div
                            id="keywords-analytics-grid"
                            className="ag-theme-alpine"
                            style={{
                                height: "calc(100vh - 150px)",
                                width: "100%",
                            }}
                        >
                            <AgGridLayout
                                setGridApi={setGridApi}
                                rowData={rowData}
                                columnDefs={columnDefs}
                                onSelectionChanged={onKeywordSelectionChanged}
                                loading={loading}
                                isTotalRowsReq={true}
                                rowHeight={gridParams?.rowHeight}
                            />
                        </div>
                    </div>
                </div>
                <AddRulesModal api={gridApi} canAddRule={canCreateRule} />
            </Layout>
        </>
    );
};

export default KeywordAnalytics;
